// Dialogo reusable para editar una unidad (Wialon o Leasecontrol) — nombre,
// UID, tipo de hardware, alias de sensor, icono y grupos, igual que el
// editor de PlataformaUnidadesPage.jsx. Se abre desde el arbol de
// ClientePlataformaPanel.jsx, donde solo se conoce un shape minimo de la
// unidad (id/nm/uid/pos/lmsg/icono_url, sin hwType/grupos/sensorAlias) — por
// eso, al abrir, primero busca la unidad completa via listUnits (mismo
// endpoint que ya usa la pagina de listado) para no mostrar el formulario
// con datos incompletos o grupos sin marcar.
import React, { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import FolderIcon from "@mui/icons-material/Folder";
import ImageIcon from "@mui/icons-material/Image";
import { getGrupoId, getGrupoNombre, mergeById, toggleId } from "../utils/plataformaGrupoHelpers";

const tableHeadBg = (theme) =>
  theme.palette.mode === "dark" ? theme.palette.background.paper : theme.palette.grey[100];

const getUnidadId = (u) => u?.id ?? u?.sys_id ?? u?.sysId;
const getUnidadNombre = (u) => u?.nm || u?.name || "-";
const getUnidadUid = (u) => u?.uid || u?.uid2 || "-";
const getUnidadGrupos = (u) => (Array.isArray(u?.grupos) ? u.grupos : []);

const getUnidadSensorAlias = (u) => {
  const sensores = u?.sens || u?.sensors || {};
  const found = Object.values(sensores).find((sensor) =>
    String(sensor?.n || sensor?.name || "").toUpperCase().startsWith("ALIAS:")
  );
  return found?.n || found?.name || "";
};

const getUnidadHwType = (u) => {
  const raw = u?.hwType || u?.hw_type || u?.hw;
  if (raw && typeof raw === "object") {
    const id = raw.id ?? raw.hw_id ?? raw.hwTypeId;
    if (!id) return null;
    return { ...raw, id, name: raw.name || raw.nombre || raw.nm || `ID ${id}` };
  }

  const id = u?.hwTypeId ?? u?.hw_type_id ?? u?.hw_id ?? u?.hw ?? u?.deviceTypeId ?? u?.device_type_id ?? u?.tipo_dispositivo_id;
  if (!id) return null;

  return {
    id,
    name: u?.hwTypeName || u?.hw_type_name || u?.hw_name || u?.hwName || u?.deviceTypeName || u?.device_type || u?.tipo_dispositivo || `ID ${id}`,
    tp: u?.hw_tp || u?.tp,
    up: u?.hw_up || u?.up,
  };
};

const getHwOptionLabel = (option) =>
  option ? `${option.name || option.nombre || option.nm || "-"} (${option.id || "-"})` : "";

const getChipOptionLabel = (option) =>
  option ? `${option.numero || "-"}${option.emai ? ` / ${option.emai}` : ""}${option.modelo ? ` - ${option.modelo}` : ""}` : "";

const emptyForm = { nombre: "", uid: "", hwTypeId: "", sensorAlias: "", iconLibId: "", iconPath: "", grupoIds: [], observacion: "" };

function GroupCheckList({ rows = [], selectedIds = [], onChange, loading = false, filter = "", onFilterChange, page = 1, totalPages = 1, onPageChange }) {
  const selectedSet = new Set(selectedIds.map((id) => String(id)));
  const visibleIds = rows.map(getGrupoId).filter((id) => id !== undefined && id !== null);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedSet.has(String(id)));

  const toggleVisible = () => {
    const next = new Set(selectedSet);
    if (allVisibleSelected) visibleIds.forEach((id) => next.delete(String(id)));
    else visibleIds.forEach((id) => next.add(String(id)));
    onChange?.([...next]);
  };

  return (
    <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 1, overflow: "hidden", gridColumn: { xs: "auto", md: "1 / span 2" } }}>
      <Box sx={{ p: 0.75, display: "flex", alignItems: "center", gap: 0.75, borderBottom: "1px solid #e5e7eb" }}>
        <Typography sx={{ fontSize: "0.78rem", fontWeight: 900, flex: 1 }}>Grupos</Typography>
        <Chip size="small" label={`${selectedSet.size} seleccionado(s)`} sx={{ height: 20, fontSize: "0.68rem" }} />
      </Box>
      <Box sx={{ p: 0.75, borderBottom: "1px solid #e5e7eb" }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Buscar grupo..."
          value={filter}
          onChange={(event) => onFilterChange?.(event.target.value)}
          InputProps={{ endAdornment: loading ? <CircularProgress color="inherit" size={16} /> : null }}
          sx={{ "& .MuiInputBase-input": { py: 0.55, fontSize: "0.76rem" } }}
        />
      </Box>
      <TableContainer sx={{ maxHeight: 220 }}>
        <Table size="small" stickyHeader sx={{ "& .MuiTableCell-root": { py: 0.35, px: 0.6, fontSize: "0.72rem" } }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ bgcolor: tableHeadBg }}>
                <Checkbox
                  size="small"
                  checked={allVisibleSelected}
                  indeterminate={!allVisibleSelected && visibleIds.some((id) => selectedSet.has(String(id)))}
                  onChange={toggleVisible}
                />
              </TableCell>
              <TableCell sx={{ bgcolor: tableHeadBg, fontWeight: 900 }}>Grupo</TableCell>
              <TableCell sx={{ bgcolor: tableHeadBg, fontWeight: 900 }}>ID</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={3} align="center" sx={{ py: 2.5 }}><CircularProgress size={18} /></TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={3} sx={{ color: "text.secondary", fontStyle: "italic" }}>Sin grupos para mostrar.</TableCell></TableRow>
            ) : (
              rows.map((grupo) => {
                const id = getGrupoId(grupo);
                const checked = selectedSet.has(String(id));
                return (
                  <TableRow key={String(id)} hover onClick={() => onChange?.(toggleId(selectedIds, id))} sx={{ cursor: "pointer" }}>
                    <TableCell padding="checkbox"><Checkbox size="small" checked={checked} /></TableCell>
                    <TableCell sx={{ fontWeight: checked ? 900 : 600 }}>{getGrupoNombre(grupo)}</TableCell>
                    <TableCell>{id || "-"}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {onPageChange && totalPages > 1 && (
        <Box sx={{ px: 0.75, py: 0.6, display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 0.75, borderTop: "1px solid #e5e7eb" }}>
          <Button size="small" disabled={loading || page <= 1} onClick={() => onPageChange(page - 1)} sx={{ minWidth: 64, fontSize: "0.7rem", py: 0.25 }}>Anterior</Button>
          <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }}>{page} / {totalPages}</Typography>
          <Button size="small" disabled={loading || page >= totalPages} onClick={() => onPageChange(page + 1)} sx={{ minWidth: 64, fontSize: "0.7rem", py: 0.25 }}>Siguiente</Button>
        </Box>
      )}
    </Box>
  );
}

export default function UnidadFormDialog({
  open,
  mode = "edit", // "create" | "edit"
  unidad, // requerido cuando mode="edit"
  onClose,
  onSaved,
  createUnit,
  updateUnit,
  listHwTypes,
  listUnitGroups,
  listIconLibrary,
  listChips,
  listUnits,
  providerColor = "#006d75",
}) {
  const [loadingUnidad, setLoadingUnidad] = useState(false);
  const [unidadCompleta, setUnidadCompleta] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState("");

  const [hwInput, setHwInput] = useState("");
  const [hwOptions, setHwOptions] = useState([]);
  const [hwLoading, setHwLoading] = useState(false);
  const [hwSelected, setHwSelected] = useState(null);

  const [groupInput, setGroupInput] = useState("");
  const [groupOptions, setGroupOptions] = useState([]);
  const [groupLoading, setGroupLoading] = useState(false);
  const [groupPage, setGroupPage] = useState(1);
  const [groupTotalPages, setGroupTotalPages] = useState(1);

  const [iconDialogOpen, setIconDialogOpen] = useState(false);
  const [iconItems, setIconItems] = useState([]);
  const [iconLoading, setIconLoading] = useState(false);
  const [iconError, setIconError] = useState("");

  const [chipInput, setChipInput] = useState("");
  const [chipOptions, setChipOptions] = useState([]);
  const [chipLoading, setChipLoading] = useState(false);
  const [chipSelected, setChipSelected] = useState(null);

  // Al abrir en modo edicion: la unidad que viene del arbol trae solo
  // id/nm/uid — se busca la version completa (con hwType/grupos/sensorAlias)
  // por UID antes de armar el formulario, igual que hace
  // PlataformaUnidadesPage.jsx con sus propias filas (que ya vienen
  // completas de listUnits). En modo creacion no hay nada que buscar.
  useEffect(() => {
    if (!open) return;

    setError("");
    setHwInput("");
    setHwOptions([]);
    setGroupInput("");
    setGroupOptions([]);
    setGroupPage(1);
    setGroupTotalPages(1);
    setChipInput("");
    setChipOptions([]);
    setChipSelected(null);

    if (mode === "create") {
      setUnidadCompleta(null);
      setForm(emptyForm);
      setHwSelected(null);
      return;
    }

    if (!unidad) return;

    const initForm = (u) => {
      const currentHwType = getUnidadHwType(u);
      setUnidadCompleta(u);
      setForm({
        nombre: getUnidadNombre(u) === "-" ? "" : getUnidadNombre(u),
        uid: getUnidadUid(u) === "-" ? "" : String(getUnidadUid(u)),
        hwTypeId: currentHwType?.id ? String(currentHwType.id) : "",
        sensorAlias: getUnidadSensorAlias(u),
        iconLibId: "",
        iconPath: "",
        grupoIds: getUnidadGrupos(u).map(getGrupoId).filter((id) => id !== undefined && id !== null).map(String),
        observacion: "",
      });
      setHwSelected(currentHwType);
      setHwInput(currentHwType ? getHwOptionLabel(currentHwType) : "");
    };

    initForm(unidad);

    if (!listUnits) return;
    const uid = getUnidadUid(unidad);
    if (uid === "-" || !uid) return;

    setLoadingUnidad(true);
    listUnits({ search: uid, page: 1, limit: 5 })
      .then((resp) => {
        const data = resp?.data?.data || [];
        const match =
          data.find((u) => String(getUnidadId(u)) === String(getUnidadId(unidad))) ||
          data.find((u) => String(getUnidadUid(u)) === String(uid)) ||
          null;
        if (match) initForm(match);
      })
      .catch(() => {})
      .finally(() => setLoadingUnidad(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, unidad]);

  useEffect(() => {
    if (!open || mode !== "create" || !listChips) return undefined;
    let active = true;
    const timer = setTimeout(async () => {
      setChipLoading(true);
      try {
        const resp = await listChips({ search: chipInput.trim(), limit: 30 });
        const payload = resp?.data || {};
        const options = Array.isArray(payload.data) ? payload.data : [];
        if (active) setChipOptions(options);
      } catch (e) {
        if (active) setChipOptions([]);
      } finally {
        if (active) setChipLoading(false);
      }
    }, 350);
    return () => { active = false; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, chipInput, listChips]);

  useEffect(() => {
    if (!open || !listHwTypes) return undefined;
    let active = true;
    const timer = setTimeout(async () => {
      setHwLoading(true);
      try {
        const resp = await listHwTypes({ search: hwInput.trim(), page: 1, limit: 30 });
        const payload = resp?.data || {};
        const options = Array.isArray(payload.data) ? payload.data : [];
        const merged = hwSelected?.id && !options.some((option) => String(option?.id) === String(hwSelected.id))
          ? [hwSelected, ...options]
          : options;
        if (active) setHwOptions(merged);
      } catch (e) {
        if (active) setHwOptions([]);
      } finally {
        if (active) setHwLoading(false);
      }
    }, 350);
    return () => { active = false; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, hwInput, listHwTypes]);

  const loadGroups = async ({ usePage = 1, useSearch = groupInput } = {}) => {
    if (!listUnitGroups) return;
    setGroupLoading(true);
    try {
      const resp = await listUnitGroups({ search: String(useSearch || "").trim(), page: usePage, limit: 50 });
      const payload = resp?.data || {};
      const data = Array.isArray(payload.data) ? payload.data : [];
      setGroupOptions(mergeById(getUnidadGrupos(unidadCompleta), data, getGrupoId));
      setGroupPage(Number(payload.page || usePage || 1));
      setGroupTotalPages(Number(payload.totalPages || 1));
    } catch (e) {
      setGroupOptions([]);
      setGroupTotalPages(1);
    } finally {
      setGroupLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !listUnitGroups) return undefined;
    const timer = setTimeout(() => loadGroups({ usePage: 1, useSearch: groupInput }), 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, groupInput, unidadCompleta]);

  const openIconLibrary = async () => {
    if (!listIconLibrary) return;
    setIconDialogOpen(true);
    setIconLoading(true);
    setIconError("");
    try {
      const resp = await listIconLibrary({ type: "unit", flags: 0, resId: 0, size: 48 });
      const payload = resp?.data?.data || resp?.data || {};
      setIconItems(Array.isArray(payload.items) ? payload.items : []);
    } catch (e) {
      setIconError(e?.response?.data?.message || e?.response?.data?.error || e.message || "No se pudo cargar la biblioteca de iconos");
    } finally {
      setIconLoading(false);
    }
  };

  const selectIcon = (item) => {
    setForm((prev) => ({ ...prev, iconLibId: "0", iconPath: item.path || "" }));
    setIconDialogOpen(false);
  };

  const handleClose = () => {
    if (updating) return;
    onClose?.();
  };

  const handleSave = async () => {
    if (mode === "create") {
      const uid = form.uid.trim();
      const hwTypeId = form.hwTypeId.trim();
      const chipNumero = chipSelected?.numero ? String(chipSelected.numero).trim() : "";
      if (!uid || !hwTypeId || !chipNumero) {
        setError(
          !uid ? "Debe indicar el UID."
            : !hwTypeId ? "Debe seleccionar el tipo de dispositivo."
              : "Debe seleccionar obligatoriamente un chip."
        );
        return;
      }

      setUpdating(true);
      setError("");
      try {
        await createUnit({
          modem: uid,
          nombre: form.nombre.trim(),
          uid,
          hwTypeId: Number(hwTypeId),
          sensorAlias: form.sensorAlias.trim(),
          iconLibId: form.iconPath.trim() ? Number(form.iconLibId || 0) : undefined,
          iconPath: form.iconPath.trim() || undefined,
          chipNumero,
          grupoIds: form.grupoIds.map(Number).filter((grupoId) => !Number.isNaN(grupoId)),
          observacion: form.observacion.trim(),
        });
        onSaved?.();
      } catch (e) {
        setError(e?.response?.data?.error || e?.response?.data?.message || e.message || "No se pudo agregar el vehiculo");
      } finally {
        setUpdating(false);
      }
      return;
    }

    const id = getUnidadId(unidadCompleta || unidad);
    if (!id || !updateUnit) {
      setError("No se pudo identificar la unidad.");
      return;
    }

    const originalName = getUnidadNombre(unidadCompleta || unidad) === "-" ? "" : getUnidadNombre(unidadCompleta || unidad);
    const originalUid = getUnidadUid(unidadCompleta || unidad) === "-" ? "" : String(getUnidadUid(unidadCompleta || unidad));
    const payload = { observacion: form.observacion.trim() };

    if (form.nombre.trim() !== originalName) payload.nombre = form.nombre.trim();
    if (form.uid.trim() !== originalUid || form.hwTypeId) payload.uid = form.uid.trim();
    if (form.hwTypeId) payload.hwTypeId = Number(form.hwTypeId);
    if (form.sensorAlias.trim()) payload.sensorAlias = form.sensorAlias.trim();
    if (form.iconPath.trim()) {
      payload.iconLibId = Number(form.iconLibId || 0);
      payload.iconPath = form.iconPath.trim();
    }
    payload.grupoIds = form.grupoIds.map(Number).filter((grupoId) => !Number.isNaN(grupoId));

    setUpdating(true);
    setError("");
    try {
      await updateUnit(id, payload);
      onSaved?.();
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.message || e.message || "No se pudo actualizar la unidad");
    } finally {
      setUpdating(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ py: 1, fontSize: "0.95rem", fontWeight: 900 }}>
          {mode === "create" ? "Agregar vehiculo" : "Editar unidad"}
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            {mode === "create"
              ? "El UID se usara para buscar el modem en equipo_modem."
              : `ID ${getUnidadId(unidadCompleta || unidad) || "-"} - ${getUnidadNombre(unidadCompleta || unidad)}`}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 1 }}>
          {!!error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
          {loadingUnidad ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={22} />
            </Box>
          ) : (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1 }}>
              <Autocomplete
                size="small"
                options={hwOptions}
                loading={hwLoading}
                value={hwSelected}
                inputValue={hwInput}
                disabled={updating}
                isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
                getOptionLabel={getHwOptionLabel}
                filterOptions={(options) => options}
                onInputChange={(_, value) => setHwInput(value)}
                onChange={(_, option) => {
                  setHwSelected(option || null);
                  setForm((prev) => ({ ...prev, hwTypeId: option?.id ? String(option.id) : "" }));
                }}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.id}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={800} sx={{ lineHeight: 1.1 }}>{option.name || "-"}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID {option.id || "-"} {option.tp ? `- TP ${option.tp}` : ""}
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={mode === "create" ? "Tipo de dispositivo *" : "Tipo de dispositivo"}
                    helperText={
                      mode === "create"
                        ? (form.hwTypeId ? `ID seleccionado: ${form.hwTypeId}` : "Busca por nombre, ID o TP")
                        : (form.hwTypeId ? `Nuevo ID: ${form.hwTypeId}` : "Opcional, solo si deseas cambiarlo")
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {hwLoading ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              <TextField
                size="small"
                label="Nombre de unidad"
                value={form.nombre}
                onChange={(e) => setForm((prev) => ({ ...prev, nombre: e.target.value }))}
                disabled={updating}
              />
              <TextField
                size="small"
                label="UID"
                value={form.uid}
                onChange={(e) => setForm((prev) => ({ ...prev, uid: e.target.value }))}
                disabled={updating}
              />
              <TextField
                size="small"
                label="Sensor alias"
                value={form.sensorAlias}
                onChange={(e) => setForm((prev) => ({ ...prev, sensorAlias: e.target.value }))}
                disabled={updating}
                helperText="Ejemplo: ALIAS:YPFB-0992"
              />
              {mode === "create" && (
                <Autocomplete
                  size="small"
                  options={chipOptions}
                  loading={chipLoading}
                  value={chipSelected}
                  inputValue={chipInput}
                  disabled={updating}
                  isOptionEqualToValue={(option, value) => String(option?.numero) === String(value?.numero)}
                  getOptionLabel={getChipOptionLabel}
                  filterOptions={(options) => options}
                  onInputChange={(_, value) => setChipInput(value)}
                  onChange={(_, option) => setChipSelected(option || null)}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.numero || option.ID}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={800} sx={{ lineHeight: 1.1 }}>
                          {option.numero || "-"} {option.emai ? `- ${option.emai}` : ""}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.modelo || "-"} | {option.ciudad || "-"} | {option.estado || "-"}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Chip relacionado *"
                      required
                      helperText="Obligatorio. Solo chips Inactivos de tu sucursal"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {chipLoading ? <CircularProgress color="inherit" size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              )}
              <Button
                variant="outlined"
                startIcon={<ImageIcon />}
                disabled={!listIconLibrary || updating}
                onClick={openIconLibrary}
                sx={{ minHeight: 40, justifyContent: "flex-start" }}
              >
                {form.iconPath ? `Icono: ${form.iconPath.split("/").pop()}` : "Abrir biblioteca de iconos"}
              </Button>
              {listUnitGroups && (
                <GroupCheckList
                  rows={mergeById(getUnidadGrupos(unidadCompleta), groupOptions, getGrupoId)}
                  selectedIds={form.grupoIds}
                  onChange={(ids) => setForm((prev) => ({ ...prev, grupoIds: ids }))}
                  loading={groupLoading}
                  filter={groupInput}
                  onFilterChange={setGroupInput}
                  page={groupPage}
                  totalPages={groupTotalPages}
                  onPageChange={(nextPage) => loadGroups({ usePage: nextPage, useSearch: groupInput })}
                />
              )}
              <TextField
                size="small"
                label="Observacion"
                value={form.observacion}
                onChange={(e) => setForm((prev) => ({ ...prev, observacion: e.target.value }))}
                disabled={updating}
                multiline
                minRows={2}
                sx={{ gridColumn: { xs: "auto", md: "1 / span 2" } }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 0.75 }}>
          <Button size="small" onClick={handleClose} disabled={updating}>Cancelar</Button>
          <LoadingButton
            size="small"
            loading={updating}
            disabled={loadingUnidad || hwLoading || groupLoading || chipLoading}
            variant="contained"
            onClick={handleSave}
            sx={{ bgcolor: providerColor, "&:hover": { bgcolor: providerColor } }}
          >
            {mode === "create" ? "Guardar" : "Guardar cambios"}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog open={iconDialogOpen} onClose={() => setIconDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ py: 1, fontSize: "0.95rem", fontWeight: 900 }}>
          Biblioteca de iconos
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>Iconos de unidades</Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 1 }}>
          {!!iconError && <Alert severity="error" sx={{ mb: 1 }}>{iconError}</Alert>}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)", md: "repeat(6, 1fr)" },
              gap: 0.75,
              minHeight: 180,
            }}
          >
            {iconLoading ? (
              <Box sx={{ gridColumn: "1 / -1", display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={20} />
              </Box>
            ) : (
              <>
                {iconItems.map((item) => (
                  <Button
                    key={item.path || item.name}
                    variant="outlined"
                    onClick={() => selectIcon(item)}
                    sx={{ minHeight: 76, p: 0.75, textTransform: "none", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 0.5, overflow: "hidden" }}
                  >
                    {item.isDir ? (
                      <FolderIcon color="warning" />
                    ) : item.imageUrl ? (
                      <Box component="img" src={item.imageUrl} alt={item.name} sx={{ width: 32, height: 32, objectFit: "contain" }} />
                    ) : (
                      <ImageIcon />
                    )}
                    <Typography variant="caption" sx={{ maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {item.name}
                    </Typography>
                  </Button>
                ))}
                {iconItems.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ gridColumn: "1 / -1", p: 2 }}>
                    No hay iconos para mostrar.
                  </Typography>
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 0.75 }}>
          <Button size="small" onClick={() => setIconDialogOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
